import { useCallback, useContext, useEffect, useState } from 'react';
import intersection from 'lodash/intersection';

import { SingleArrowLeft } from '@r1-frontend/ui-react/components/svg/arrows';
import ChannelsFilters from '@r1-frontend/ui-react/components/Channels/ChannelsFilters';
import ChannelsList from '@r1-frontend/ui-react/components/Channels/ChannelsList';
import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { IOption } from '@r1-frontend/ui-react/components/select/select';
import Throbber from '@r1-frontend/ui-react/components/loaders/Throbber';

import { useMatchMediaByWidth } from '@r1-frontend/shared/hooks/useMatchMedia';

import { LandingContext } from '~/src/containers/ContextContainer';

import { IChannelsDto, IFormattedChannelThemeDto } from '~/src/services/api/api-epg/v1/dto/IChannelsDto';
import { getChannels } from '~/src/services/api/api-epg/v1/channels';
import { getCategories } from '~/src/services/api/api-epg/v1/channel-categories';

import { getChannelPackages } from './helpers/getChannelPackages';
import { getChannelThemesWithCounts } from './helpers/getChannelThemesWithCounts';

import * as ST from './styled';

interface IChannelListPopup {
    freePackets?: number | string | number[],
    onClose: () => void,
    isOfferChannels?: boolean,
}

export const defaultChannelsFilter: IFormattedChannelThemeDto = {
    id: 0,
    label: '',
    name: '',
    value: 0,
    weight: -1,
    channels: [],
};

const ChannelListPopup = ({ freePackets, onClose }: IChannelListPopup): JSX.Element => {
    const [isShowSearch, setIsShowSearch] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [channelsFiltersApply, setIsChannelsFiltersIsApply] = useState(false);

    const [filter, setFilter] = useState<IFormattedChannelThemeDto>(defaultChannelsFilter);
    const [channelThemes, setChannelThemes] = useState<IFormattedChannelThemeDto[]>([]);
    const [preparedThemes, setPreparedThemes] = useState<IFormattedChannelThemeDto[]>([]);

    const [searchValue, setSearchValue] = useState('');
    const [allChannels, setAllChannels] = useState<IChannelsDto[]>([]);
    const [filteredChannels, setFilteredChannels] = useState<IChannelsDto[]>([]);
    const [tariffChannels, setTariffChannels] = useState<IChannelsDto[]>([]);

    const isMobile = useMatchMediaByWidth(wideBreakpoints.mobile);

    const { city: { provider_id: providerId } } = useContext(LandingContext);

    const getAllChannels = async () => {
        if (!allChannels?.length) {
            const response = await getChannels(providerId, { with: 'category,channelInfo,packets' });

            if (response.isSuccess) {
                const data = response.payload.map((channel) => {
                    return ({
                        ...channel,
                        button: channel.lcn,
                        logo: channel?.channelInfo?.logo,
                        chid: channel.channelId,
                        xvid: channel.epgId,
                        display: channel.display.toString(),
                        bids: channel.packets.map(({ billing_id }) => (billing_id || '').toString()),
                        thid: (channel?.category?.id || '').toString(),
                    });
                });
                setAllChannels(data);
            }
        }
    };

    const getChannelsThemes = async () => {
        if (!channelThemes?.length) {
            const response = await getCategories(providerId, ({ with: 'channels' }));

            if (response.isSuccess) {
                const formattedThemes = response.payload.map((theme) => ({
                    value: theme.rndId,
                    id: theme.rndId,
                    label: theme.name,
                    name: theme.name,
                    weight: theme.weight,
                    channels: (theme.channels || []).map(channel => ({
                        ...channel,
                        isHd: channel.quality === 'HD',
                        id: channel.epgId,
                        logo: channel?.channelInfo?.logo,
                    })),
                }));
                setChannelThemes(formattedThemes);
            }
        }
    };

    const onShowSearchInput = useCallback((e) => {
        e.stopPropagation();
        setIsShowSearch(true);
        setFilter(defaultChannelsFilter);
    }, []);

    const onChangeChannelTheme = (option: IOption<IFormattedChannelThemeDto>) => {
        const value = preparedThemes.find(item => item.label === option.label);
        setFilter(value);

        if (value.id === 0) {
            setFilteredChannels(tariffChannels);
        } else {
            const filteredCategoriesIds = tariffChannels.filter((channel) => channel.categoryId.toString() === value.id.toString());
            setFilteredChannels(filteredCategoriesIds);
        }
    };

    const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value.toLowerCase();
        setSearchValue(inputValue);

        const filteredTitles = tariffChannels.filter(channel => channel.title.toLowerCase().includes(inputValue));
        setFilteredChannels(filteredTitles);

        if (!filteredTitles.length) {
            setIsChannelsFiltersIsApply(true);
        }
    };

    const onClearSearch = () => {
        setSearchValue('');
        setFilter(defaultChannelsFilter);
        setFilteredChannels(tariffChannels);
        setIsChannelsFiltersIsApply(false);
    };

    const onClickPopupWrapper = useCallback(() => {
        setIsShowSearch(false);
    }, []);

    const onCloseHandler = () => {
        onClearSearch();
        onClose();
    };

    useEffect(() => {
        if (allChannels?.length) {
            setIsLoading(false);

            if (freePackets) {
                const channelPackages = getChannelPackages(freePackets);
                const filteredTariffChannels = allChannels.filter(channel => !!intersection(channelPackages.packages, channel.bids).length);
                setTariffChannels(filteredTariffChannels);
            }
            return;
        }

        getAllChannels();
        getChannelsThemes();
    }, [providerId, allChannels, freePackets]);

    useEffect(() => {
        if (!!tariffChannels.length && !!channelThemes.length) {
            const channelsCountThemes = getChannelThemesWithCounts(tariffChannels, channelThemes);
            setPreparedThemes(channelsCountThemes);
            setFilteredChannels(tariffChannels);
        }
    }, [tariffChannels, channelThemes]);

    return (
        <ST.SBasePopup
            isOpen={true}
            width='600px'
            height='645px'
            title={isMobile && isShowSearch
                ? <ST.HideSearchBtn
                    bType={'ghost'}
                    Icon={<SingleArrowLeft />}
                    onClick={onClearSearch}
                >
                    Назад
                </ST.HideSearchBtn>
                : 'Список телеканалов'
            }
            description={
                <ChannelsFilters
                    isShowSearch={isShowSearch}
                    channels={allChannels}
                    selectedFilter={filter}
                    preparedThemes={preparedThemes}
                    onChangeChannelTheme={onChangeChannelTheme}
                    onShowSearchInput={onShowSearchInput}
                    onClearSearch={onClearSearch}
                    onChangeSearch={onChangeSearch}
                    value={searchValue ?? filter.label}
                />
            }
            onClose={onCloseHandler}
            onClick={onClickPopupWrapper}
            data-test='channel-list-popup'
        >
            {isLoading
                ? <Throbber position='absolute' />
                : <ChannelsList
                    channelsFiltersIsApply={channelsFiltersApply}
                    channels={filteredChannels}
                />
            }
        </ST.SBasePopup>
    );
};

export default ChannelListPopup;
